import DOMPurify from 'dompurify';
import { Marked } from 'marked';

// The editor's valid_elements drops anything else without a word, so the
// conversion targets exactly what it accepts.
const BLOCK_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'sup',
  'ul',
  'ol',
  'li',
];

const INLINE_TAGS = ['strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sup'];

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const converter = new Marked({
  gfm: true,
  renderer: {
    heading({ tokens }) {
      return `<p><strong>${this.parser.parseInline(tokens)}</strong></p>`;
    },
    blockquote({ tokens }) {
      return this.parser.parse(tokens);
    },
    code({ text }) {
      return `<p>${escapeHtml(text)}</p>`;
    },
    codespan({ text }) {
      return escapeHtml(text);
    },
    link({ tokens }) {
      return this.parser.parseInline(tokens);
    },
    image({ text }) {
      return escapeHtml(text || '');
    },
    hr() {
      return '';
    },
    table(token) {
      const rows = [token.header, ...token.rows];
      return rows
        .map(
          row =>
            `<p>${row
              .map(cell => this.parser.parseInline(cell.tokens))
              .join(' — ')}</p>`,
        )
        .join('');
    },
  },
});

function normalize(text: string): string {
  return (text || '').replace(/\r\n?/g, '\n');
}

export function markdownToHtml(text: string): string {
  // Convert [SOURCE_1] or [SOURCE_1, SOURCE_2] to superscript references <sup>[1]</sup>, <sup>[1, 2]</sup>
  const formattedText = (text || '').replace(
    /\[\s*SOURCE_(\d+)(?:\s*,\s*SOURCE_(\d+))*\s*\]/gi,
    match => {
      const numbers = Array.from(match.matchAll(/SOURCE_(\d+)/gi), m => m[1]);
      return `<sup>[${numbers.join(', ')}]</sup>`;
    },
  );

  const html = converter.parse(normalize(formattedText), { async: false });
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: BLOCK_TAGS,
    ALLOWED_ATTR: [],
  })
    .replace(/>\n+</g, '><')
    .trim();
}

export function markdownToInline(text: string): string {
  const flattened = markdownToHtml(text).replace(
    /<\/(p|li|h[1-6])>|<br\s*\/?>/g,
    ' ',
  );

  return DOMPurify.sanitize(flattened, {
    ALLOWED_TAGS: INLINE_TAGS,
    ALLOWED_ATTR: [],
  })
    .replace(/\s+/g, ' ')
    .trim();
}
